"use client";

import { useState } from "react";
import { ArrowDown } from "lucide-react";
import BlogCard from "@/components/blog/BlogCard";
import AnswerBox from "@/components/shared/AnswerBox";
import type { BlogCardData, BlogCategoryInfo } from "@/lib/blog";

const PAGE_SIZE = 6;

/**
 * Inhalt der Ratgeber-Kategorie-Seiten (/ratgeber/zulassung/ …).
 * Bekommt nur schlanke Kartendaten – die Artikel-Bodies bleiben auf dem Server.
 */
export default function BlogListContent({
  category,
  articles,
}: {
  category: BlogCategoryInfo;
  articles: BlogCardData[];
}) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const results = articles;
  const visible = results.slice(0, visibleCount);
  const remaining = results.length - visible.length;

  return (
    <>
      {/* Hero */}
      <section className="relative bg-paper overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 60% 55% at 50% 0%, oklch(94.5% 0.026 268 / 0.9) 0%, transparent 70%)",
          }}
        />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-18 pb-12 sm:pb-14">
          {/* CSS entrance (.enter): the <h1> is the LCP element and must be
              visible from the first paint, not only after hydration. */}
          <div className="flex flex-col items-center text-center gap-5">
            <h1 className="enter font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.1] text-ink">
              Ratgeber: <span className="text-accent">{category.name}</span>
            </h1>

            <p className="enter [--enter-delay:90ms] text-lg text-ink-soft leading-relaxed max-w-2xl">
              {category.description}
            </p>
          </div>
        </div>
      </section>

      {/* AEO-Kurzantwort direkt unter dem Hero */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-2">
        <AnswerBox
          question={`Was findest du im Ratgeber „${category.name}“?`}
          answer={`${category.description} In dieser Kategorie findest du aktuell ${results.length} ausführliche Ratgeber-Artikel mit konkreten Schritten und Beispielen rund um die Medizin-Zulassung.`}
          keyTakeaways={
            results.length > 0
              ? results.slice(0, 3).map((article) => article.title)
              : undefined
          }
          audience="Für Studieninteressierte, die sich Schritt für Schritt in das Bewerbungs- und Auswahlverfahren für ein Medizinstudium einarbeiten möchten."
        />
      </div>

      {/* Artikel-Grid */}
      <section className="bg-paper border-t border-line">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <p className="text-sm text-ink-soft mb-6" aria-live="polite">
            <span className="font-bold text-ink tnum">{results.length}</span> Artikel in
            „{category.name}“
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6 items-stretch">
            {visible.map((article, i) => (
              <BlogCard key={article.slug} article={article} index={i} />
            ))}
          </div>

          {remaining > 0 && (
            <div className="flex flex-col items-center gap-3 mt-10">
              <button
                onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-line bg-white text-ink text-sm font-semibold hover:border-accent hover:text-accent transition-colors duration-200 cursor-pointer min-h-[48px]"
              >
                Weitere Artikel laden
                <ArrowDown size={15} aria-hidden="true" />
              </button>
              <p className="text-xs text-ink-faint tnum">
                {visible.length} von {results.length} angezeigt
              </p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
